import { acceptHMRUpdate, defineStore, getActivePinia } from 'pinia'
import {
  AuthStatus,
  type LoginParams,
  type PasswordChangeParams,
  type PasswordResetParams,
  type RequestPasswordResetParams,
  type Token
} from '~/types/auth'
import statusMessages from '/@lang/status-messages.json'
import type { ApiResponse } from '#shared/types/utils'
import { jwtDecode, type JwtPayload } from 'jwt-decode'
import type { UserId } from '#shared/types/users'
import { cloneDeep } from 'es-toolkit'
import { createUseApi } from '~/composables/base/createUseApi.ts'
import { usePersist } from '~/plugins/pinia.ts'

interface CustomJwtPayload extends JwtPayload {
  is_admin?: boolean
}

const authStatusMessages = statusMessages['auth-status-messages']

export const useAuthStore = defineStore(
  'auth',
  () => {
    const localeStore = useLocaleStore()

    const token = ref<Token>()
    const admin = ref<Token>()

    const status = ref<AuthStatus>(AuthStatus.UNAUTHORIZED)

    const isAuthenticated = computed(
      () => token.value !== undefined && status.value === AuthStatus.AUTHORIZED
    )

    const authorizationHeader = computed(() =>
      token.value?.accessToken ? `Bearer ${token.value.accessToken}` : undefined
    )

    const hasAdminRole = computed(() => {
      if (admin.value) {
        const adminDecodedToken = jwtDecode<CustomJwtPayload>(admin.value.accessToken)
        return !!adminDecodedToken?.is_admin
      }

      if (!token.value) {
        return false
      }

      const decodedToken = jwtDecode<CustomJwtPayload>(token.value.accessToken)

      return !!decodedToken?.is_admin
    })

    const setAccessToken = (incomingToken: string) => {
      if (token.value) {
        token.value.accessToken = incomingToken
      }
    }

    const onLogin = (incomingToken: Token) => {
      token.value = incomingToken
      status.value = AuthStatus.AUTHORIZED
    }

    const onLogoutHandler = () => {
      if (admin.value) {
        token.value = cloneDeep(admin.value)
        admin.value = undefined
      } else {
        token.value = undefined
        status.value = AuthStatus.UNAUTHORIZED
      }

      const pinia = getActivePinia()
      // Pinia hides this property, so we sadly have to cast to any
      const stores = (pinia as any)?._s as
        | Map<string, { onLogout?: () => void }>
        | undefined
      stores?.forEach((store) => {
        store.onLogout?.()
      })
    }

    const api = createUseApi({
      getAuthHeader: () => authorizationHeader.value,
      setAccessToken: (token: string) => setAccessToken(token),
      logout: () => onLogoutHandler()
    })

    const login = async (loginParams: LoginParams): Promise<boolean> => {
      try {
        const response = await api.post<ApiResponse<Token>>('auth/login', loginParams)

        onLogin(response.data)
        return true
      } catch {
        addToast().error(authStatusMessages[localeStore.locale].error.login)
        return false
      }
    }

    const loginAsUser = async (userId: UserId): Promise<false | Token> => {
      try {
        const response = await api.get<ApiResponse<Token>>(`auth/login-as/${userId}`, {
          headers: new Headers({
            Authorization: `Bearer ${admin.value?.accessToken ?? token.value?.accessToken}`
          })
        })

        if (!admin.value) {
          admin.value = cloneDeep(token.value)
        }

        onLogin(response.data)

        return response.data
      } catch {
        return false
      }
    }

    const logout = async (): Promise<boolean> => {
      if (!admin.value) {
        status.value = AuthStatus.LOGGING_OUT
      }

      try {
        await api.post<{ message: string }>('auth/logout')
        return true
      } catch {
        return false
      } finally {
        onLogoutHandler()
      }
    }

    const requestPasswordReset = async (
      params: RequestPasswordResetParams
    ): Promise<boolean> => {
      try {
        await api.post('auth/reset-request', params)
        addToast().success(
          authStatusMessages[localeStore.locale].success['request-password-reset']
        )
        return true
      } catch {
        addToast().error(
          authStatusMessages[localeStore.locale].error['request-password-reset']
        )
        return false
      }
    }

    const resetPassword = async (params: PasswordResetParams): Promise<boolean> => {
      try {
        await api.post('auth/reset-password', params)
        addToast().success(
          authStatusMessages[localeStore.locale].success['reset-password']
        )
        return true
      } catch {
        addToast().error(authStatusMessages[localeStore.locale].error['reset-password'])
        return false
      }
    }

    const changePassword = async (
      params: PasswordChangeParams
    ): Promise<boolean | 'incorrect-password'> => {
      try {
        await api.post('auth/change-password', params)
        addToast().success(
          authStatusMessages[localeStore.locale].success['change-password']
        )
        return true
      } catch (error: any) {
        if (error?._data?.message === 'incorrect_password') {
          addToast().error(
            authStatusMessages[localeStore.locale].error['change-password'][
              'incorrect_password'
            ]
          )
          return 'incorrect-password'
        } else {
          addToast().error(
            authStatusMessages[localeStore.locale].error['change-password'][
              'correct_password'
            ]
          )
          return false
        }
      }
    }

    const sendNewVerificationEmail = async (userId: UserId): Promise<boolean> => {
      try {
        await api.post(`auth/send-new-verify-email/${userId}`)
        addToast().success(
          authStatusMessages[localeStore.locale].success['send-new-verification-email']
        )
        return true
      } catch {
        addToast().error(
          authStatusMessages[localeStore.locale].error['send-new-verification-email']
        )
        return false
      }
    }

    const verifyEmail = async (userId: UserId, hash: string): Promise<boolean> => {
      try {
        await api.post(`auth/verify-email/${userId}/${hash}`)
        return true
      } catch {
        addToast().error(authStatusMessages[localeStore.locale].error['verify-email'])
        return false
      }
    }

    return {
      token,
      admin,
      status,

      isAuthenticated,
      authorizationHeader,
      hasAdminRole,

      setAccessToken,
      onLogin,
      onLogoutHandler,

      login,
      loginAsUser,
      logout,
      requestPasswordReset,
      resetPassword,
      changePassword,
      sendNewVerificationEmail,
      verifyEmail
    }
  },
  {
    persist: usePersist({
      storage: piniaPluginPersistedstate.cookies()
    })
  }
)

/**
 * Pinia supports Hot Module replacement, so you can edit your stores and
 * interact with them directly in your app without reloading the page.
 *
 * @see https://pinia.esm.dev/cookbook/hot-module-replacement.html
 * @see https://vitejs.dev/guide/api-hmr.html
 */
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
